import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useAuth } from '../context/AuthContext';
import { GET_CUSTOMER_DATA, CREATE_CUSTOMER_ADDRESS, UPDATE_CUSTOMER_ADDRESS, DELETE_CUSTOMER_ADDRESS } from '../queries/customerQueries';
import AccountNavigation from './AccountNavigation';
import Loader from './Loader';
import './MyAccount.css';

const AccountAddresses = () => {
  const { user, logout, isInitializing } = useAuth();
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [addressEditMode, setAddressEditMode] = useState('add');
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressFormData, setAddressFormData] = useState({
    firstname: '',
    lastname: '',
    street: ['', ''],
    city: '',
    region_id: '',
    postcode: '',
    country_code: 'US',
    telephone: '',
    default_shipping: false,
    default_billing: false
  });
  const [addressError, setAddressError] = useState('');
  const [addressSuccess, setAddressSuccess] = useState('');

  const { data: customerData, loading: customerLoading, error: customerError, refetch: refetchCustomerData } = useQuery(GET_CUSTOMER_DATA);
  const [createCustomerAddress] = useMutation(CREATE_CUSTOMER_ADDRESS);
  const [updateCustomerAddress] = useMutation(UPDATE_CUSTOMER_ADDRESS);
  const [deleteCustomerAddress] = useMutation(DELETE_CUSTOMER_ADDRESS);

  if (isInitializing) {
    return (
      <div className="my-account-container">
        <Loader fullScreen={true} size="large" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="my-account-container">
        <div className="my-account-error">
          <h2>Access Denied</h2>
          <p>Please log in to access your account.</p>
        </div>
      </div>
    );
  }

  const handleOpenAddAddress = () => {
    setAddressEditMode('add');
    setEditingAddress(null);
    setAddressFormData({
      firstname: '',
      lastname: '',
      street: ['', ''],
      city: '',
      region_id: '',
      postcode: '',
      country_code: 'US',
      telephone: '',
      default_shipping: false,
      default_billing: false
    });
    setIsAddressModalOpen(true);
    setAddressError('');
    setAddressSuccess('');
  };

  const handleOpenEditAddress = (address) => {
    setAddressEditMode('edit');
    setEditingAddress(address);
    setAddressFormData({
      firstname: address.firstname || '',
      lastname: address.lastname || '',
      street: address.street || ['', ''],
      city: address.city || '',
      region_id: address.region?.region_id || '',
      postcode: address.postcode || '',
      country_code: address.country_code || 'US',
      telephone: address.telephone || '',
      default_shipping: address.default_shipping || false,
      default_billing: address.default_billing || false
    });
    setIsAddressModalOpen(true);
    setAddressError('');
    setAddressSuccess('');
  };

  const handleCloseAddressModal = () => {
    setIsAddressModalOpen(false);
    setAddressEditMode('add');
    setEditingAddress(null);
    setAddressFormData({
      firstname: '',
      lastname: '',
      street: ['', ''],
      city: '',
      region_id: '',
      postcode: '',
      country_code: 'US',
      telephone: '',
      default_shipping: false,
      default_billing: false
    });
    setAddressError('');
    setAddressSuccess('');
  };

  const handleAddressInputChange = (field, value) => {
    if (field === 'street') {
      setAddressFormData(prev => ({
        ...prev,
        street: value
      }));
    } else {
      setAddressFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSaveAddress = async () => {
    try {
      setAddressError('');
      setAddressSuccess('');

      if (!addressFormData.firstname.trim() || !addressFormData.lastname.trim() ||
          !addressFormData.street[0].trim() || !addressFormData.city.trim() ||
          !addressFormData.postcode.trim() || !addressFormData.telephone.trim()) {
        setAddressError('Please fill in all required fields (First Name, Last Name, Street, City, Postal Code, and Phone Number).');
        return;
      }

      if (addressFormData.country_code === 'US' && !addressFormData.region_id) {
        setAddressError('Please select a state for US addresses.');
        return;
      }

      const addressInput = {
        firstname: addressFormData.firstname.trim(),
        lastname: addressFormData.lastname.trim(),
        street: addressFormData.street.filter(s => s.trim()).map(s => s.trim()),
        city: addressFormData.city.trim(),
        postcode: addressFormData.postcode.trim(),
        country_code: addressFormData.country_code,
        telephone: addressFormData.telephone.trim(),
        default_shipping: addressFormData.default_shipping,
        default_billing: addressFormData.default_billing
      };

      if (addressFormData.region_id) {
        addressInput.region = {
          region_id: parseInt(addressFormData.region_id)
        };
      }

      if (addressEditMode === 'edit' && editingAddress) {
        await updateCustomerAddress({
          variables: {
            id: parseInt(editingAddress.id),
            input: addressInput
          }
        });
        setAddressSuccess('Address updated successfully!');
      } else {
        await createCustomerAddress({
          variables: { input: addressInput }
        });
        setAddressSuccess('Address created successfully!');
      }

      await refetchCustomerData();

      setTimeout(() => {
        setAddressSuccess('');
        handleCloseAddressModal();
      }, 2000);
    } catch (error) {
      console.error('Error saving address:', error);
      setAddressError(error.message || 'Failed to save address. Please try again.');
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }

    try {
      await deleteCustomerAddress({
        variables: { id: parseInt(addressId) }
      });

      await refetchCustomerData();
      setAddressSuccess('Address deleted successfully!');
      setTimeout(() => setAddressSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting address:', error);
      setAddressError(error.message || 'Failed to delete address. Please try again.');
      setTimeout(() => setAddressError(''), 5000);
    }
  };

  const renderAddressModal = () => {
    if (!isAddressModalOpen) return null;

    return (
      <div className="modal-overlay" onClick={handleCloseAddressModal}>
        <div className="address-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{addressEditMode === 'edit' ? 'Edit Address' : 'Add New Address'}</h2>
            <button className="modal-close" onClick={handleCloseAddressModal}>×</button>
          </div>

          <div className="modal-content">
            {addressSuccess && <div className="success-message">{addressSuccess}</div>}
            {addressError && <div className="error-message">{addressError}</div>}

            <div className="address-form">
              <div className="address-form-grid">
                <div className="form-group">
                  <label htmlFor="addressFirstname">First Name *</label>
                  <input
                    type="text"
                    id="addressFirstname"
                    value={addressFormData.firstname}
                    onChange={(e) => handleAddressInputChange('firstname', e.target.value)}
                    placeholder="Enter first name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="addressLastname">Last Name *</label>
                  <input
                    type="text"
                    id="addressLastname"
                    value={addressFormData.lastname}
                    onChange={(e) => handleAddressInputChange('lastname', e.target.value)}
                    placeholder="Enter last name"
                    required
                  />
                </div>
                <div className="form-group form-group-full">
                  <label htmlFor="addressStreet1">Street Address *</label>
                  <input
                    type="text"
                    id="addressStreet1"
                    value={addressFormData.street[0]}
                    onChange={(e) => {
                      const newStreet = [...addressFormData.street];
                      newStreet[0] = e.target.value;
                      handleAddressInputChange('street', newStreet);
                    }}
                    placeholder="Enter street address"
                    required
                  />
                </div>
                <div className="form-group form-group-full">
                  <label htmlFor="addressStreet2">Apartment, suite, etc.</label>
                  <input
                    type="text"
                    id="addressStreet2"
                    value={addressFormData.street[1] || ''}
                    onChange={(e) => {
                      const newStreet = [...addressFormData.street];
                      newStreet[1] = e.target.value;
                      handleAddressInputChange('street', newStreet);
                    }}
                    placeholder="Apartment, suite, etc. (optional)"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="addressCity">City *</label>
                  <input
                    type="text"
                    id="addressCity"
                    value={addressFormData.city}
                    onChange={(e) => handleAddressInputChange('city', e.target.value)}
                    placeholder="Enter city"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="addressRegion">State/Region {addressFormData.country_code === 'US' ? '*' : ''}</label>
                  {addressFormData.country_code === 'US' ? (
                    <select
                      id="addressRegion"
                      value={addressFormData.region_id}
                      onChange={(e) => handleAddressInputChange('region_id', e.target.value)}
                      required={addressFormData.country_code === 'US'}
                    >
                      <option value="">Select State</option>
                      <option value="1">Alabama</option>
                      <option value="2">Alaska</option>
                      <option value="3">Arizona</option>
                      <option value="4">Arkansas</option>
                      <option value="5">California</option>
                      <option value="6">Colorado</option>
                      <option value="7">Connecticut</option>
                      <option value="8">Delaware</option>
                      <option value="9">Florida</option>
                      <option value="10">Georgia</option>
                      <option value="11">Hawaii</option>
                      <option value="12">Idaho</option>
                      <option value="13">Illinois</option>
                      <option value="14">Indiana</option>
                      <option value="15">Iowa</option>
                      <option value="16">Kansas</option>
                      <option value="17">Kentucky</option>
                      <option value="18">Louisiana</option>
                      <option value="19">Maine</option>
                      <option value="20">Maryland</option>
                      <option value="21">Massachusetts</option>
                      <option value="22">Michigan</option>
                      <option value="23">Minnesota</option>
                      <option value="24">Mississippi</option>
                      <option value="25">Missouri</option>
                      <option value="26">Montana</option>
                      <option value="27">Nebraska</option>
                      <option value="28">Nevada</option>
                      <option value="29">New Hampshire</option>
                      <option value="30">New Jersey</option>
                      <option value="31">New Mexico</option>
                      <option value="32">New York</option>
                      <option value="33">North Carolina</option>
                      <option value="34">North Dakota</option>
                      <option value="35">Ohio</option>
                      <option value="36">Oklahoma</option>
                      <option value="37">Oregon</option>
                      <option value="38">Pennsylvania</option>
                      <option value="39">Rhode Island</option>
                      <option value="40">South Carolina</option>
                      <option value="41">South Dakota</option>
                      <option value="42">Tennessee</option>
                      <option value="43">Texas</option>
                      <option value="44">Utah</option>
                      <option value="45">Vermont</option>
                      <option value="46">Virginia</option>
                      <option value="47">Washington</option>
                      <option value="48">West Virginia</option>
                      <option value="49">Wisconsin</option>
                      <option value="50">Wyoming</option>
                    </select>
                  ) : (
                    <input
                      type="text"
                      id="addressRegion"
                      value={addressFormData.region_id}
                      onChange={(e) => handleAddressInputChange('region_id', e.target.value)}
                      placeholder="Enter state or region"
                    />
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="addressPostcode">Postal Code *</label>
                  <input
                    type="text"
                    id="addressPostcode"
                    value={addressFormData.postcode}
                    onChange={(e) => handleAddressInputChange('postcode', e.target.value)}
                    placeholder="Enter postal code"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="addressCountry">Country</label>
                  <select
                    id="addressCountry"
                    value={addressFormData.country_code}
                    onChange={(e) => handleAddressInputChange('country_code', e.target.value)}
                  >
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                    <option value="GB">United Kingdom</option>
                    <option value="DE">Germany</option>
                    <option value="FR">France</option>
                    <option value="AU">Australia</option>
                  </select>
                </div>
                <div className="form-group form-group-full">
                  <div className="checkbox-group">
                    <div className="checkbox-item">
                      <input
                        type="checkbox"
                        id="defaultShipping"
                        checked={addressFormData.default_shipping}
                        onChange={(e) => handleAddressInputChange('default_shipping', e.target.checked)}
                      />
                      <label htmlFor="defaultShipping" className="checkbox-label">Set as default shipping address</label>
                    </div>
                    <div className="checkbox-item">
                      <input
                        type="checkbox"
                        id="defaultBilling"
                        checked={addressFormData.default_billing}
                        onChange={(e) => handleAddressInputChange('default_billing', e.target.checked)}
                      />
                      <label htmlFor="defaultBilling" className="checkbox-label">Set as default billing address</label>
                    </div>
                  </div>
                </div>
                <div className="form-group form-group-full">
                  <label htmlFor="addressTelephone">Phone Number *</label>
                  <input
                    type="tel"
                    id="addressTelephone"
                    value={addressFormData.telephone}
                    onChange={(e) => handleAddressInputChange('telephone', e.target.value)}
                    placeholder="Enter phone number"
                    required
                  />
                </div>
              </div>
              <div className="address-actions">
                <button
                  className="btn-primary"
                  onClick={handleSaveAddress}
                  disabled={!addressFormData.firstname.trim() || !addressFormData.lastname.trim() ||
                           !addressFormData.street[0].trim() || !addressFormData.city.trim() ||
                           !addressFormData.postcode.trim() || !addressFormData.telephone.trim() ||
                           (addressFormData.country_code === 'US' && !addressFormData.region_id)}
                >
                  {addressEditMode === 'edit' ? 'Update Address' : 'Add Address'}
                </button>
                <button className="btn-secondary" onClick={handleCloseAddressModal}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="my-account-container">
      <div className="account-header">
        <h1>My Account</h1>
        {customerData?.customer?.firstname ? (
          <p>Welcome back, {customerData.customer.firstname}!</p>
        ) : ('')}
        <button className="logout-btn" onClick={logout}>
          Logout
        </button>
      </div>

      <div className="account-content">
        <AccountNavigation />
        <main className="account-main">
          <div className="account-section">
            {addressSuccess && <div className="success-message">{addressSuccess}</div>}
            {addressError && <div className="error-message">{addressError}</div>}
            {customerLoading ? (
              <div className="loading">Loading addresses...</div>
            ) : customerError ? (
              <div className="error">Error loading addresses: {customerError.message}</div>
            ) : (
              <div className="addresses-grid">
                {customerData?.customer?.addresses?.length > 0 ? (
                customerData.customer.addresses.map((address) => (
                  <div key={address.id} className="address-card">
                    <div className="address-header">
                      <h4>{address.firstname} {address.lastname}</h4>
                    </div>
                    <div className="address-details">
                      <p>{address.street?.join(', ')}</p>
                      <p>{address.city}, {address.region?.region || ''} {address.postcode}</p>
                      <p>{address.country_code}</p>
                      {address.telephone && <p>Phone: {address.telephone}</p>}
                      {address.company && <p>Company: {address.company}</p>}
                    </div>
                    <div className="address-badges">
                      {address.default_shipping && <span className="badge">Default Shipping</span>}
                      {address.default_billing && <span className="badge">Default Billing</span>}
                    </div>
                    <div className="address-actions">
                      <button className="btn-secondary" onClick={() => handleOpenEditAddress(address)}>Edit</button>
                      <button className="btn-danger" onClick={() => handleDeleteAddress(address.id)}>Delete</button>
                    </div>
                  </div>
                ))
                ) : (
                  <div className="empty-state">
                    <p>No addresses found</p>
                  </div>
                )}
              </div>
            )}
            <button className="btn-primary" onClick={handleOpenAddAddress}>Add New Address</button>
          </div>
        </main>
      </div>

      {renderAddressModal()}
    </div>
  );
};

export default AccountAddresses;